import { FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import PenSvg from "@/assets/svg/PenSvg";
import ProfileSvg from "@/assets/svg/ProfileSvg";
import BluePlusSvg from "@/assets/svg/BluePlusSvg";

import theme from "@/theme";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useAppSelector } from "@/hooks/useAppSelector";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useUpdateUserInfoMutation } from "@/store/api/authApi";
import { useUploadImageMutation } from "@/store/api/uploadFileApi";
import { setAuthData } from "@/store/slice/authSlice";

import { useGetUserByUidQuery } from "@/store/api/userApi";
import { Navigate } from "react-router-dom";
import CustomToast from "@/components/UI/CustomToast/CustomToast";
import { socialOptions } from "@/pages/Settings/component/socialIcons";
import { AccountInfoSchema, schemaAccountInformation } from "@/pages/Settings/subpage/schema";
import CloseSvg from "@/assets/svg/CloseSvg";
import { styles } from "./styles";
import CaretArrowSvg from "@/assets/svg/CaretArrowSvg";
import ArrowHorizontal from "@/assets/svg/ArrowHorizontal";
import TradingStyle from "./components/TradingStyle";
import TradingMentors from "./components/TradingMentors";
import SocialMedia from "./components/SocialMedia";
import AddCustomLink from "./components/AddCustomLink";
import BasketSvg from "@/assets/svg/BasketSvg";
import BlackPenSvg from "@/assets/svg/BlackPen";

type FormInput = AccountInfoSchema;
type ModalTab = "main" | "tradingStyle" | "tradingMentors" | "socialMedia";

interface IDeleteModalProps {
  open: boolean;
  handleClose: () => void;
  onDelete: () => void;
}

interface ProfileMenuItemProps {
  label: string;
  value: string;
  onClick: () => void;
}

const ProfileMenuItem = ({ label, value, onClick }: ProfileMenuItemProps) => (
  <Box
    display="flex"
    flexDirection="row"
    justifyContent="space-between"
    alignItems="center"
    onClick={onClick}
    sx={{
      cursor: "pointer",
      paddingBlock: "8px",
      "&:hover": {
        backgroundColor: "#F9FCFE",
      },
    }}
  >
    <Box display="flex" flexDirection="column" gap="2px">
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "15px",
          lineHeight: "24px",
          letterSpacing: 0,
          fontFamily: "Inter",
          color: theme.palette.custom.blueGray900,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "13px",
          lineHeight: "20px",
          letterSpacing: 0,
          fontFamily: "Inter",
          color: theme.palette.custom.blueGray600,
        }}
      >
        {value}
      </Typography>
    </Box>
    <CaretArrowSvg height={24} width={24} />
  </Box>
);

const tabTitles: Record<ModalTab, string> = {
  main: "Edit Profile",
  tradingStyle: "Trading Style",
  tradingMentors: "Trading Mentors",
  socialMedia: "Social Media",
};

const EditProfileModal: FC<IDeleteModalProps> = ({ open, handleClose }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [activeTab, setActiveTab] = useState<ModalTab>("main");

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormInput>({
    resolver: yupResolver(schemaAccountInformation),
    mode: "onChange",
  });

  const { user: authUser } = useAppSelector((state) => state.auth);
  if (!authUser) return <Navigate to="/" replace />;

  const { data: user } = useGetUserByUidQuery(authUser.uid, {
    skip: !authUser?.uid,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  type SocialKey = "twitter" | "facebook" | "instagram" | "linkedin";
  const [toast, setToast] = useState<{ message: string; success: boolean } | null>(null);
  const [selectedMentors, setSelectedMentors] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imageDeleted, setImageDeleted] = useState<boolean>(false);
  const [updateUserInfo] = useUpdateUserInfoMutation();
  const [uploadImage] = useUploadImageMutation();
  const dispatch = useAppDispatch();
  const watchBio = watch("bio");
  const [socialFields, setSocialFields] = useState<{ key: SocialKey; url: string }[]>([]);
  const [currentMentorInput, setCurrentMentorInput] = useState("");
  const [imagePreview, setImagePreview] = useState<string | null>(
    user?.image?.url ? user?.image?.url : null
  );
  const [tradingStyle, setTradingStyle] = useState<string>(user?.tradingStyle || "");
  const [customLinksState, setCustomLinksState] = useState<{ title: string; url: string }[]>([
    { title: "", url: "" },
  ]);
  const [initialCustomLinks, setInitialCustomLinks] = useState<{ title: string; url: string }[]>([
    { title: "", url: "" },
  ]);

  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  const isAvatarMenuOpen = Boolean(avatarMenuAnchor);

  const initialFormValues = useRef<{
    form: FormInput;
    tradingStyle: string;
    selectedMentors: string;
    imageFile: File | null;
    socialFields: { key: SocialKey; url: string }[];
    customLinks: { title: string; url: string }[];
  }>({
    form: { username: "", firstName: "", lastName: "", email: "", bio: "" },
    tradingStyle: "",
    selectedMentors: "",
    imageFile: null,
    socialFields: [],
    customLinks: [],
  });

  useEffect(() => {
    if (!open) {
      setActiveTab("main");
      setImageFile(null);
      setImageDeleted(false);
      const originalImageUrl = toSafeUrl(user?.image?.url, user?.createdAt ?? null);
      setImagePreview(originalImageUrl);
    }

    const resetLinks =
      initialCustomLinks.length > 0 ? initialCustomLinks : [{ title: "", url: "" }];
    setCustomLinksState(resetLinks);
    setIsValidUrls([]);
  }, [open, user, initialCustomLinks]);

  const isFormInvalid = !!Object.keys(errors).length;

  const handleSelectImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPEG, PNG or JPG are allowed.");
      return;
    }
    if (file.size > maxSize) {
      alert("File size must be less than 5MB.");
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setImageDeleted(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const hasChanges = (): boolean => {
    if (imageFile !== null) return true;
    if (imageDeleted && user?.image?.url) return true;

    const initialBio = initialFormValues.current.form.bio || "";
    const currentBio = watchBio || "";
    if (currentBio.trim() !== initialBio.trim()) return true;

    if (tradingStyle !== initialFormValues.current.tradingStyle) return true;

    const currentMentors = selectedMentors.trim();
    const initialMentors = initialFormValues.current.selectedMentors.trim();
    if (currentMentors !== initialMentors) return true;

    if (currentMentorInput.trim()) return true;

    const initialSocialUrls = initialFormValues.current.socialFields
      .map((f) => `${f.key}:${f.url}`)
      .sort()
      .join("|");
    const currentSocialUrls = socialFields
      .map((f) => `${f.key}:${f.url}`)
      .sort()
      .join("|");
    if (currentSocialUrls !== initialSocialUrls) return true;

    const currentCustom = customLinksState
      .filter((l) => l.title.trim() || l.url.trim())
      .map((l) => `${l.title}:${l.url}`)
      .sort()
      .join("|");
    const initialCustomFiltered = initialFormValues.current.customLinks
      .filter((l) => l.title.trim() || l.url.trim())
      .map((l) => `${l.title}:${l.url}`)
      .sort()
      .join("|");

    if (currentCustom !== initialCustomFiltered) return true;

    return false;
  };

  const areCustomLinksValid = (): boolean => {
    return customLinksState.every((link) => {
      if (!link.url.trim()) return true;
      return isValidUrl(link.url);
    });
  };

  const isSaveDisabled = isFormInvalid || !hasChanges() || !areCustomLinksValid();

  useEffect(() => {
    if (user) {
      const formValues: FormInput = {
        username: user.username || "",
        firstName: user.name || "",
        lastName: user.surname || "",
        email: user.email || "",
        bio: user.bio || "",
      };

      reset(formValues);
      setTradingStyle(user.tradingStyle || "");
      setSelectedMentors(user.tradingMentors?.trim() ?? "");
      setCurrentMentorInput("");

      const standardLinks = user.socialMedia?.filter((link) => !link.title) ?? [];
      const customLinksFromApi = user.socialMedia
        ? user.socialMedia
            .filter((link) => !!link.title)
            .map((link) => ({ url: link.url ?? "", title: link.title ?? "" }))
        : [];

      const linksToSet =
        customLinksFromApi.length > 0 ? customLinksFromApi : [{ title: "", url: "" }];

      setInitialCustomLinks(linksToSet);
      setCustomLinksState(linksToSet.map((l) => ({ ...l })));

      const allPlatforms: SocialKey[] = ["twitter", "instagram", "linkedin", "facebook"];
      const socialFieldsFromApi = allPlatforms.map((platform) => {
        const existingLink = standardLinks.find((link) => {
          const url = link.url?.toLowerCase() ?? "";
          if (platform === "twitter") return url.includes("x.com") || url.includes("twitter.com");
          return url.includes(`${platform}.com`);
        });
        return { key: platform, url: existingLink?.url ?? "" };
      });

      setSocialFields(socialFieldsFromApi.map((f) => ({ ...f })));

      initialFormValues.current = {
        form: { ...formValues },
        tradingStyle: user.tradingStyle || "",
        selectedMentors: user.tradingMentors?.trim() ?? "",
        imageFile: null,
        socialFields: socialFieldsFromApi.map((f) => ({ ...f })),
        customLinks: linksToSet.map((l) => ({ ...l })),
      };
    }
  }, [user, reset]);

  const onSubmit = async (data: FormInput) => {
    if (!user?.uid) return;
    const mainSocialLinks = socialFields
      .filter((f) => f.url.trim())
      .map((f) => ({ url: f.url.trim() }));

    const customLinks = customLinksState
      .filter((link) => link.url.trim())
      .map((link) => ({
        url: link.url.trim(),
        title: link.title?.trim() || undefined,
      }));

    const socialMediaLinks = [...mainSocialLinks, ...customLinks];

    let finalMentors = selectedMentors.trim();
    const currentInput = currentMentorInput.trim();

    if (currentInput) {
      finalMentors = finalMentors
        ? finalMentors + (finalMentors.endsWith(";") ? " " : "; ") + currentInput
        : currentInput;
    }

    if (imageFile) {
      const formData = new FormData();
      formData.append("file", imageFile);
      await uploadImage(formData).unwrap();
    }

    try {
      const response = await updateUserInfo({
        uid: user.uid,
        data: {
          tradingMentors: finalMentors,
          bio: (data.bio ?? "").trim(),
          tradingStyle,
          updatedAt: new Date().toISOString(),
          socialMedia: socialMediaLinks,
        },
      }).unwrap();

      setToast({
        message: "Profile updated successfully",
        success: true,
      });
      dispatch(setAuthData({ user: response }));
      handleClose();
    } catch (err) {
      console.error("Update failed:", err);
      setToast({
        message: "Failed to update account information",
        success: false,
      });
    }
  };

  const toSafeUrl = (url?: string, ver?: string | number | null) => {
    if (!url) return null;
    const safe = encodeURI(url);
    const v = ver ?? Date.now();
    return safe + (safe.includes("?") ? "&" : "?") + "v=" + v;
  };

  useEffect(() => {
    const next = toSafeUrl(user?.image?.url, user?.createdAt ?? null);
    setImagePreview(next);
  }, [user?.image?.url, user?.createdAt]);

  const handleBack = () => {
    setActiveTab("main");
  };

  const handleSave = () => {
    handleSubmit(onSubmit)();
  };

  const getTradingStyleDisplay = () => {
    return tradingStyle || "Not selected";
  };

  const getTradingMentorsDisplay = () => {
    return selectedMentors || "Not selected";
  };

  const getSocialMediaDisplay = () => {
    return "Manage your Social media links";
  };

  const handleAvatarMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAvatarMenuAnchor(event.currentTarget);
  };

  const handleAvatarMenuClose = () => {
    setAvatarMenuAnchor(null);
  };

  const handleChoosePhoto = () => {
    handleAvatarMenuClose();
    fileInputRef.current?.click();
  };

  const handleDeletePhoto = () => {
    handleAvatarMenuClose();
    setImageFile(null);
    setImageDeleted(true);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAddCustomLink = () => {
    setCustomLinksState((prev) => [...prev, { title: "", url: "" }]);
    setIsValidUrls((prev) => [...prev, true]);
  };

  const [isValidUrls, setIsValidUrls] = useState<boolean[]>([]);

  const handleUrlChange = (data: { title: string; url: string }[]) => {
    setCustomLinksState(data);

    const validations = data.map((link) => {
      if (!link.url) return true;
      return isValidUrl(link.url);
    });
    setIsValidUrls(validations);
  };

  const renderHeader = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        pb: "12px",
        width: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
        {activeTab === "main" ? (
          <IconButton
            onClick={handleClose}
            size="small"
            aria-label="close"
            sx={{ padding: 0, height: "fit-content" }}
          >
            <CloseSvg />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleBack}
            size="small"
            aria-label="back"
            sx={{ padding: 0, height: "fit-content" }}
          >
            <ArrowHorizontal width="19px" height="13px" />
          </IconButton>
        )}
      </Box>

      <Typography
        sx={{
          fontWeight: 500,
          fontSize: "16px",
          lineHeight: "22px",
          fontFamily: "Inter",
          color: theme.palette.custom.blueGray900,
          flex: 1,
          textAlign: "center",
        }}
      >
        {tabTitles[activeTab]}
      </Typography>

      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
        {activeTab === "main" ? (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={isSaveDisabled}
            sx={{
              bgcolor: theme.palette.custom.mainBlue,
              borderRadius: "28px",
              minWidth: "75px",
              height: "32px",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.custom.mainBlue,
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#8BC5F4",
                color: "#FFFFFF",
              },
            }}
          >
            Save
          </Button>
        ) : (
          <Typography
            component="span"
            onClick={isSaveDisabled ? undefined : handleSave}
            sx={{
              fontFamily: "Inter",
              fontWeight: 600,
              fontSize: "15px",
              lineHeight: "24px",
              color: isSaveDisabled ? "#8BC5F4" : theme.palette.custom.mainBlue,
              cursor: isSaveDisabled ? "default" : "pointer",
            }}
          >
            Save
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderMainContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Box width="84px" height="84px" borderRadius="50%" display="flex" position="relative">
        {imagePreview ? (
          <>
            <img
              src={imagePreview ?? ""}
              alt="Avatar"
              onError={() => setImagePreview(null)}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderRadius: "50%",
              }}
            />
            <input
              ref={fileInputRef}
              id="avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              style={{ display: "none" }}
              onChange={handleSelectImage}
            />
            <IconButton
              onClick={handleAvatarMenuOpen}
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 25,
                height: 25,
                p: 0,
                backgroundColor: "transparent",
              }}
            >
              <PenSvg size="small" />
            </IconButton>
            <Menu
              anchorEl={avatarMenuAnchor}
              open={isAvatarMenuOpen}
              onClose={handleAvatarMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: 0,
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "box-shadow: 0px 0px 16px 0px #0000001A",
                  minWidth: "164px",
                  mt: "4px",
                  bgcolor: theme.palette.custom.mainWhite,
                  padding: "8px",
                },
              }}
            >
              <MenuItem
                onClick={handleChoosePhoto}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  color: theme.palette.custom.blueGray900,
                  py: "8px",
                  gap: "8px",
                  px: "0px",
                  "&:hover": {
                    backgroundColor: "#F9FCFE",
                  },
                }}
              >
                <BlackPenSvg width={20} height={20} />
                Choose photo
              </MenuItem>
              <MenuItem
                onClick={handleDeletePhoto}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  lineHeight: "20px",
                  fontWeight: 400,
                  color: "#EF4444",
                  py: "8px",
                  px: "0px",
                  gap: "8px",
                  "&:hover": {
                    backgroundColor: "#FEF2F2",
                  },
                }}
              >
                <BasketSvg width={20} height={20} />
                Remove photo
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <span
              style={{
                width: "7.5rem",
                height: "7.5rem",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-34%,-34%)",
              }}
            >
              <ProfileSvg />
            </span>
            <input
              ref={fileInputRef}
              id="avatar-upload"
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              style={{ display: "none" }}
              onChange={handleSelectImage}
            />
            <IconButton
              onClick={handleAvatarMenuOpen}
              sx={{
                position: "absolute",
                right: 0,
                bottom: 0,
                width: 25,
                height: 25,
                p: 0,
                backgroundColor: "transparent",
              }}
            >
              <PenSvg size="small" />
            </IconButton>
            <Menu
              anchorEl={avatarMenuAnchor}
              open={isAvatarMenuOpen}
              onClose={handleAvatarMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: 0,
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              PaperProps={{
                sx: {
                  borderRadius: "10px",
                  boxShadow: "box-shadow: 0px 0px 16px 0px #0000001A",
                  minWidth: "164px",
                  mt: "4px",
                  bgcolor: theme.palette.custom.mainWhite,
                  padding: "8px",
                },
              }}
            >
              <MenuItem
                onClick={handleChoosePhoto}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  color: theme.palette.custom.blueGray900,
                  py: "8px",
                  gap: "8px",
                  px: "0px",
                  "&:hover": {
                    backgroundColor: "#F9FCFE",
                  },
                }}
              >
                <BlackPenSvg width={20} height={20} />
                Choose photo
              </MenuItem>
              <MenuItem
                onClick={handleDeletePhoto}
                sx={{
                  fontSize: "14px",
                  fontFamily: "Inter",
                  lineHeight: "20px",
                  fontWeight: 400,
                  color: "#EF4444",
                  py: "8px",
                  px: "0px",
                  gap: "8px",
                  "&:hover": {
                    backgroundColor: "#FEF2F2",
                  },
                }}
              >
                <BasketSvg width={20} height={20} />
                Remove photo
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <FormControl fullWidth sx={{ position: "relative", mt: "4px", flex: 1, display: "flex" }}>
          <InputLabel shrink sx={styles.inputLabel}>
            Bio
          </InputLabel>
          <TextField
            multiline
            placeholder="Tell more about yourself..."
            fullWidth
            defaultValue={user?.bio ?? ""}
            {...register("bio")}
            error={!!errors.bio}
            helperText={errors.bio?.message}
            InputProps={{
              ...styles.TextFieldProps,
              sx: {
                ...((styles.TextFieldProps as any)?.sx || {}),
                height: "166px",
                alignItems: "flex-start",
                "& .MuiInputBase-input": {
                  height: "166px !important",
                  overflow: "auto !important",
                },
              },
            }}
            sx={{
              flex: 1,
              display: "flex",
              "& .MuiOutlinedInput-root": {
                flex: 1,
                caretColor: "#E4E7E9",
                "& fieldset": {
                  borderColor: "#E4E7E9",
                },
                "&:hover fieldset": {
                  borderColor: "#E4E7E9",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#E4E7E9",
                },
              },
            }}
          />
        </FormControl>

        <Box sx={{ mt: "8px" }}>
          <ProfileMenuItem
            label="Trading Style"
            value={getTradingStyleDisplay()}
            onClick={() => setActiveTab("tradingStyle")}
          />
          <ProfileMenuItem
            label="Trading Mentors"
            value={getTradingMentorsDisplay()}
            onClick={() => setActiveTab("tradingMentors")}
          />
          <ProfileMenuItem
            label="Social Media"
            value={getSocialMediaDisplay()}
            onClick={() => setActiveTab("socialMedia")}
          />
        </Box>
      </Box>
    </Box>
  );

  const renderTradingStyleContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <TradingStyle
        defaultValue={tradingStyle}
        onSelect={(style: string) => setTradingStyle(style)}
      />
    </Box>
  );

  const renderTradingMentorsContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <TradingMentors
        onSelect={(mentors: string) => setSelectedMentors(mentors)}
        defaultValue={selectedMentors}
        onInputChange={(value) => setCurrentMentorInput(value)}
        inputValue={currentMentorInput}
      />
    </Box>
  );

  const renderSocialMediaContent = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {socialFields.length > 0 && (
          <Typography
            sx={{
              fontSize: "15px",
              lineHeight: "24px",
              fontWeight: 500,
              letterSpacing: 0,
              color: theme.palette.custom.blueGray900,
              mb: "2px",
            }}
          >
            Add social media
          </Typography>
        )}
        {socialFields.map((field, index) => {
          const option = socialOptions.find((s) => s.key === field.key);
          const isApiValue = !!user?.socialMedia?.some((link) => link.url === field.url);

          return (
            <SocialMedia
              key={index}
              platform={field.key}
              icon={option?.icon ? <option.icon /> : undefined}
              value={field.url}
              isFromApi={isApiValue}
              onChange={(newUrl) => {
                const next = [...socialFields];
                next[index].url = newUrl;
                setSocialFields(next);
              }}
            />
          );
        })}
      </Box>

      <AddCustomLink links={customLinksState} onChange={handleUrlChange} isValidUrl={isValidUrls} />

      <Box sx={styles.buttonBox}>
        <Button variant="outlined" sx={styles.addMore} onClick={handleAddCustomLink}>
          <BluePlusSvg />
          Add one more
        </Button>
      </Box>
    </Box>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "tradingStyle":
        return renderTradingStyleContent();
      case "tradingMentors":
        return renderTradingMentorsContent();
      case "socialMedia":
        return renderSocialMediaContent();
      default:
        return renderMainContent();
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "640px",
            height: "608px",
            maxHeight: "608px",
            bgcolor: theme.palette.custom.mainWhite,
            boxShadow: "0 32px 48px -8px rgba(0, 0, 0, 0.1), 0 0 14px -4px rgba(0, 0, 0, 0.05)",
            padding: "16px",
            borderRadius: "1.5rem",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            overflowY: "auto",
          }}
        >
          {renderHeader()}
          {renderContent()}
        </Box>
      </Modal>

      {toast && (
        <CustomToast
          message={toast.message}
          success={toast.success}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default EditProfileModal;
